import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ShoppingBag, Shield, Cookie, ChevronDown, ChevronUp, ArrowRight, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface PoliciesPageProps {
  onOpenSampleModal?: () => void;
}
const policies = [{
  icon: ShoppingBag,
  title: 'Ordering policy',
  description: 'How orders work, lead times, and what to expect.',
  href: '#ordering',
  content: 'Standard products ship within 5–7 working days. Bespoke orders depend on complexity and artwork approval, typically 10–14 days. Lead times are confirmed at checkout. All orders are subject to our standard terms and conditions.'
}, {
  icon: Shield,
  title: 'How we protect your information',
  description: 'Details on data handling and your privacy rights.',
  href: '#privacy',
  content: 'We collect only the information necessary to process your order and provide our services. Your data is never sold to third parties. We comply fully with UK GDPR and the Data Protection Act 2018. You have the right to access, correct, or delete your personal data at any time.'
}, {
  icon: Cookie,
  title: 'Cookie and tracking preferences',
  description: 'Understand how we use cookies on this site.',
  href: '#cookies',
  content: 'We use essential cookies to ensure the site functions correctly, and optional analytics cookies to understand how visitors use our site. You can manage your cookie preferences at any time. We do not use cookies for advertising or tracking across other websites.'
}];
const faqs = [{
  question: 'How long do orders take?',
  answer: 'Standard products ship within 5–7 working days. Bespoke orders depend on complexity and artwork approval, typically 10–14 days. Lead times are confirmed at checkout.'
}, {
  question: 'Can I cancel or amend my order?',
  answer: 'Orders can be amended or cancelled within 24 hours of placement, provided production has not yet started. For bespoke orders, cancellation may not be possible once artwork has been approved and production begun.'
}, {
  question: 'What is your returns policy?',
  answer: 'We accept returns on standard stock items within 14 days of delivery, provided they are unused and in original condition. Bespoke and custom-printed items cannot be returned unless they are defective or do not match the approved proof.'
}, {
  question: 'How do you handle my personal data?',
  answer: 'We process your personal data only to fulfil your order and provide customer support. We do not sell or share your data with third parties for marketing purposes. Full details are in our Privacy Policy.'
}, {
  question: 'Do you use cookies?',
  answer: 'Yes, we use essential cookies for site functionality and optional analytics cookies. You can manage your preferences via our cookie banner or settings page at any time.'
}];
export function PoliciesPage({
  onOpenSampleModal
}: PoliciesPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-1103">
      <Header onOpenSampleModal={onOpenSampleModal} data-id="element-1104" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-1105">
        {/* Hero */}
        <section className="py-16 md:py-24 bg-white text-center" data-id="element-1106">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1107">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1108">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0A1628] tracking-tight leading-[1.08] mb-6" data-id="element-1109">
                Our policies
              </h1>
              <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1110">
                We operate with transparency and integrity in everything we do
                for our customers
              </p>
            </motion.div>
          </div>
        </section>

        {/* Policy Cards */}
        <section className="py-16 md:py-20 bg-slate-50" data-id="element-1111">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1112">
            <div className="text-center mb-12" data-id="element-1113">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block" data-id="element-1114">
                Documents
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-3" data-id="element-1115">
                Our policies and terms
              </h2>
              <p className="text-lg text-slate-500" data-id="element-1116">
                Read the full details of how we operate.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1117">
              {policies.map((policy, index) => {
              const Icon = policy.icon;
              return <motion.div key={policy.title} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col" data-id="element-1118">
                    <div className="h-10 w-10 bg-slate-100 rounded-xl flex items-center justify-center mb-6" data-id="element-1119">
                      <Icon className="h-5 w-5 text-[#0A1628]" data-id="element-1120" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1121">
                      {policy.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1" data-id="element-1122">
                      {policy.description}
                    </p>
                    <div className="flex items-center gap-3" data-id="element-1123">
                      <a href={policy.href} className="px-4 py-2 bg-[#0A1628] text-white text-sm font-semibold rounded-full hover:bg-[#0052CC] transition-colors" data-id="element-1124">
                        Read
                      </a>
                      <a href={policy.href} className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-[#0052CC] transition-colors" data-id="element-1125">
                        <ArrowRight className="h-4 w-4" data-id="element-1126" />
                      </a>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* Questions CTA */}
        <section className="py-16 md:py-20 bg-white text-center" data-id="element-1127">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1128">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-1129">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-1130">
                Questions about our terms?
              </h2>
              <p className="text-lg text-slate-500 mb-10 max-w-xl mx-auto" data-id="element-1131">
                Get in touch if anything needs clarification or you need more
                detail.
              </p>
              <div className="flex flex-wrap justify-center gap-3" data-id="element-1132">
                <a href="/contact" className="px-6 py-3 bg-[#0A1628] text-white font-semibold rounded-full hover:bg-[#0052CC] transition-colors" data-id="element-1133">
                  Contact
                </a>
                <a href="mailto:sales@industriallabels.co.uk" className="px-6 py-3 border border-slate-200 text-[#0A1628] font-semibold rounded-full hover:border-[#0052CC] hover:text-[#0052CC] transition-colors" data-id="element-1134">
                  Enquire
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Image Banner */}
        <section className="bg-slate-100" data-id="element-1135">
          <div className="max-w-[1440px] mx-auto" data-id="element-1136">
            <div className="aspect-[21/6] overflow-hidden" data-id="element-1137">
              <img src="https://cdn.magicpatterns.com/uploads/qszDzSmxSCirrNe3REUbpj/nameplates-industial.webp" alt="Industrial manufacturing" className="w-full h-full object-cover" data-id="element-1138" />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-24 bg-white" data-id="element-1139">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1140">
            <div className="max-w-3xl mx-auto" data-id="element-1141">
              <div className="text-center mb-12" data-id="element-1142">
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-1143">
                  Questions
                </h2>
                <p className="text-lg text-slate-500" data-id="element-1144">
                  Find answers to common questions about our policies and how we
                  work.
                </p>
              </div>

              <div className="space-y-3" data-id="element-1145">
                {faqs.map((faq, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.05
              }} className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1146">
                    <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-slate-50 transition-colors" data-id="element-1147">
                      <span className="font-semibold text-[#0A1628]" data-id="element-1148">
                        {faq.question}
                      </span>
                      {openFaq === index ? <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" data-id="element-1149" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" data-id="element-1150" />}
                    </button>
                    <AnimatePresence data-id="element-1151">
                      {openFaq === index && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-1152">
                          <div className="px-6 pb-5 pt-1 text-slate-600 leading-relaxed border-t border-slate-100" data-id="element-1153">
                            {faq.answer}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1154" />
    </div>;
}